import { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { CheckboxRadio } from 'shared/components/CheckboxRadio';

import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { FeatureFlags } from 'featureFlags/constants';
import { variableFontRegular, variableFontMedium } from 'styles/theme/text';
import { formatMoneyObject } from 'shared/utils/formatMoneyObject';
import { t } from 'localization/utils/t';
import { localizeShippingMethod } from 'checkout/utils/localizeShippingMethod';

const TP = 'checkout.components.Shipping.ShippingMethods';

export const ShippingMethods = () => {
  const { order, changeShippingServiceLevel } = useCheckoutContext();
  const forceShowShippingTab = useFeatureFlag(FeatureFlags.TEMP_WEB_FORCE_SHOW_SHIPPING_TAB);
  const { isMobile } = useWindowSize();
  const headerRef = useRef<HTMLDivElement>(null);

  const availableShippingMethods = order?.shippingServiceLevelOptions?.filter(
    (option) => option.available,
  );
  const selectedOption = availableShippingMethods?.find(
    (option) => option.id === order?.shippingServiceLevel,
  );
  const hasManyAvailableShippingMethods = availableShippingMethods?.length > 1;

  useEffect(() => {
    if (forceShowShippingTab && hasManyAvailableShippingMethods && isMobile && headerRef?.current) {
      headerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [forceShowShippingTab, hasManyAvailableShippingMethods, headerRef, isMobile]);

  return (
    <>
      <ShippingMethods.Header data-qa="checkout_shipping_methods_header" ref={headerRef}>
        {t(`${TP}.shippingMethodTitle`, 'Shipping Method')}
      </ShippingMethods.Header>
      <ShippingMethods.Description data-qa="checkout_shipping_methods_description">
        {selectedOption?.description}
      </ShippingMethods.Description>
      <ShippingMethods.Fieldset>
        {availableShippingMethods?.map((option) => (
          <ShippingMethods.Wrapper htmlFor={option.id} key={option.id} aria-label={option.name}>
            <ShippingMethods.Radio>
              <CheckboxRadio
                type="radio"
                id={option.id}
                name="shipping-method"
                value={option.id}
                aria-label={option.name}
                onChange={() => changeShippingServiceLevel(option.id)}
                checked={option.id === order.shippingServiceLevel}
                data-qa="checkout_shipping_methods_radio_button"
              />
            </ShippingMethods.Radio>
            <span>
              <ShippingMethods.Label>
                <span data-qa="checkout_shipping_method_name">
                  {localizeShippingMethod(option.name)}
                </span>
                :{' '}
                <span data-qa="checkout_shipping_method_price">
                  {formatMoneyObject(option.localizedPriceCents)}
                </span>
              </ShippingMethods.Label>
              {option.estimation && (
                <ShippingMethods.Label data-qa="checkout_shipping_method_estimation">
                  {option.estimation}
                </ShippingMethods.Label>
              )}
            </span>
          </ShippingMethods.Wrapper>
        ))}
      </ShippingMethods.Fieldset>
      {selectedOption?.disclaimer && (
        <ShippingMethods.Disclaimer data-qa="checkout_shipping_method_disclaimer">
          {selectedOption?.disclaimer}
        </ShippingMethods.Disclaimer>
      )}
    </>
  );
};

ShippingMethods.Header = styled.p`
  ${variableFontMedium({ size: '11.5px' })};
  padding-bottom: 0;
  margin: 0 0 4px 0;
  padding-top: 42px;
`;

ShippingMethods.Description = styled.p`
  margin: 0;
  padding-bottom: 22px;
  ${variableFontRegular({ size: '9.5px' })};

  @media (min-width: ${({ theme }) => theme.breakpoints.small}px) {
    ${variableFontRegular({ size: '11.5px' })};
  }
`;

ShippingMethods.Fieldset = styled.fieldset`
  border: none;
  padding: 0;
`;

ShippingMethods.Wrapper = styled.label`
  color: ${({ theme }) => theme.colors.black};
  display: grid;
  grid-gap: 16px;
  grid-template-columns: min-content auto;
  padding-bottom: 32px;
  cursor: pointer;

  :last-child {
    padding-bottom: 0;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.small}px) {
    font-size: 11.5px;
  }
`;

ShippingMethods.Radio = styled.span`
  display: flex;

  input {
    opacity: 0;
    width: 0;
    height: 0;
    padding: 0;

    &:focus + span {
      ${({ theme }) => css`
        box-shadow: 0 0 0 0.05em ${theme.colors.white}, 0 0 0.15em 0.1em ${theme.colors.black};
      `}
    }
  }

  input + span::before {
    content: '';
    width: 10px;
    height: 12px;
    box-shadow: inset 12px 12px ${({ theme }) => theme.colors.black};
    border-radius: 0;
    transition: 100ms transform ease-in-out;
    transform: scale(0);
  }

  input:checked + span::before {
    transform: scale(1);
    position: relative;
    top: -1px;
  }
`;

ShippingMethods.RadioControl = styled.span`
  border-radius: 0;
  border: 1px solid ${({ theme }) => theme.colors.black};
  height: 12px;
  transform: translateY(3px);
  width: 12px;
  display: grid;
  place-items: center;
`;

ShippingMethods.Label = styled.p`
  margin: 2px 0 0 0;
  ${variableFontRegular({ size: '11.5px' })};
`;

ShippingMethods.Disclaimer = styled.p`
  margin: 0;
  padding: 32px 0;
`;
